import { createTheme } from "@mui/material/styles";
// Палитра построена вокруг глубокого зелёного: продукт про еду и питание,
// но зелёный приглушённый, чтобы часами смотреть на таблицы было не больно.
// Нейтрали с лёгким зелёным подтоном — чистый серый рядом с акцентом выглядит
// грязным.
const LIGHT = {
    bg: "#f5f7f5",
    paper: "#ffffff",
    paperSunk: "#eff2ef",
    rowHover: "#f3f6f3",
    textPrimary: "#16211c",
    textSecondary: "#5b6b63",
    divider: "#e2e7e3",
    primary: "#1e6f4e",
    primaryHover: "#175a3f",
    primarySoft: "#e7f1ec",
    secondary: "#b4612b",
    success: "#2e7d5b",
    warning: "#b07d1c",
    error: "#b3402f",
    info: "#2f6b8f",
};
const DARK = {
    bg: "#0f1512",
    paper: "#161d19",
    paperSunk: "#121814",
    // В тёмной теме подсветка строки должна быть светлее фона, а не темнее
    rowHover: "rgba(255, 255, 255, 0.04)",
    textPrimary: "#e8eeea",
    textSecondary: "#96a69d",
    divider: "#253029",
    primary: "#5fc194",
    primaryHover: "#7ad0a8",
    primarySoft: "#18261f",
    secondary: "#d98b54",
    success: "#5fc194",
    warning: "#d9ab5a",
    error: "#e07a68",
    info: "#6fa8c9",
};
export const SIDEBAR_WIDTH = 248;
export const TOPBAR_HEIGHT = 60;
export function buildTheme(mode) {
    const c = mode === "light" ? LIGHT : DARK;
    return createTheme({
        palette: {
            mode,
            primary: { main: c.primary, dark: c.primaryHover, light: c.primarySoft },
            secondary: { main: c.secondary },
            success: { main: c.success },
            warning: { main: c.warning },
            error: { main: c.error },
            info: { main: c.info },
            background: { default: c.bg, paper: c.paper },
            text: { primary: c.textPrimary, secondary: c.textSecondary },
            divider: c.divider,
        },
        shape: { borderRadius: 12 },
        typography: {
            fontFamily: '"Manrope", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
            h1: { fontWeight: 800, letterSpacing: "-0.03em" },
            h2: { fontWeight: 800, letterSpacing: "-0.025em" },
            h3: { fontWeight: 700, letterSpacing: "-0.02em" },
            h4: { fontWeight: 700, letterSpacing: "-0.02em", fontSize: "1.75rem" },
            h5: { fontWeight: 700, letterSpacing: "-0.015em", fontSize: "1.3rem" },
            h6: { fontWeight: 700, letterSpacing: "-0.01em", fontSize: "1.05rem" },
            subtitle1: { fontWeight: 600 },
            subtitle2: { fontWeight: 600 },
            body2: { lineHeight: 1.55 },
            button: { fontWeight: 600, letterSpacing: 0 },
            overline: { fontWeight: 700, letterSpacing: "0.09em" },
        },
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    body: {
                        backgroundColor: c.bg,
                        // Тонкий скроллбар: в админке их много и толстые серые полосы
                        // визуально дробят таблицы
                        scrollbarColor: `${c.divider} transparent`,
                        scrollbarWidth: "thin",
                    },
                    "::-webkit-scrollbar": { width: 10, height: 10 },
                    "::-webkit-scrollbar-thumb": {
                        backgroundColor: c.divider,
                        borderRadius: 8,
                        border: `2px solid ${c.bg}`,
                    },
                    "::-webkit-scrollbar-thumb:hover": {
                        backgroundColor: c.textSecondary,
                    },
                },
            },
            MuiPaper: {
                defaultProps: { elevation: 0 },
                styleOverrides: {
                    root: { backgroundImage: "none" },
                    outlined: { borderColor: c.divider },
                },
            },
            MuiCard: {
                defaultProps: { elevation: 0 },
                styleOverrides: {
                    root: { border: `1px solid ${c.divider}`, borderRadius: 14 },
                },
            },
            MuiButton: {
                defaultProps: { disableElevation: true },
                styleOverrides: {
                    root: { textTransform: "none", borderRadius: 10, paddingInline: 16 },
                    sizeSmall: { paddingInline: 12 },
                    containedPrimary: {
                        "&:hover": { backgroundColor: c.primaryHover },
                    },
                },
            },
            MuiIconButton: {
                styleOverrides: { root: { borderRadius: 10 } },
            },
            MuiToggleButton: {
                styleOverrides: {
                    root: {
                        textTransform: "none",
                        fontWeight: 600,
                        borderColor: c.divider,
                        "&.Mui-selected": {
                            backgroundColor: c.primarySoft,
                            color: c.primary,
                        },
                    },
                },
            },
            MuiChip: {
                styleOverrides: {
                    root: { borderRadius: 8, fontWeight: 600 },
                    sizeSmall: { height: 22 },
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: { borderRadius: 10 },
                    notchedOutline: { borderColor: c.divider },
                },
            },
            MuiTableContainer: {
                styleOverrides: { root: { borderRadius: 14 } },
            },
            MuiTableCell: {
                styleOverrides: {
                    root: {
                        borderBottomColor: c.divider,
                        padding: "11px 16px",
                    },
                    head: {
                        fontSize: 11.5,
                        fontWeight: 700,
                        letterSpacing: "0.07em",
                        textTransform: "uppercase",
                        color: c.textSecondary,
                        backgroundColor: c.paperSunk,
                        whiteSpace: "nowrap",
                    },
                    // Цифры в колонках должны выстраиваться по разрядам
                    body: { fontVariantNumeric: "tabular-nums" },
                },
            },
            MuiTableRow: {
                styleOverrides: {
                    root: {
                        "&:last-of-type td": { borderBottom: 0 },
                        "&:hover": { backgroundColor: c.rowHover },
                    },
                },
            },
            MuiDialog: {
                styleOverrides: { paper: { borderRadius: 16 } },
            },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: { borderRadius: 8, fontSize: 12.5, padding: "6px 10px" },
                },
            },
            MuiAlert: {
                styleOverrides: { root: { borderRadius: 12 } },
            },
            MuiListItemButton: {
                styleOverrides: {
                    root: {
                        borderRadius: 10,
                        "&.Mui-selected": {
                            backgroundColor: c.primarySoft,
                            color: c.primary,
                            "&:hover": { backgroundColor: c.primarySoft },
                        },
                    },
                },
            },
            MuiDrawer: {
                styleOverrides: {
                    paper: {
                        borderRight: `1px solid ${c.divider}`,
                        backgroundColor: c.paper,
                    },
                },
            },
        },
    });
}
